import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { Box, IconButton, Rating, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import SearchBar from "../../components/SearchBar";
import usePermission from "../../hooks/usePermission";
import { GridColDef } from "@mui/x-data-grid";
import TableComponent from "../../components/TableComponent";
import useTranslation from "../../hooks/traslation";
import {
  useDeleteRatingsIdMutation,
  useLazyGetRatingsQuery,
} from "../../services/ratings";
import moment from "moment";
import { isValidInput } from "../../utils/validations";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/confirmationModal";

const ManageReviews = () => {
  const navigate = useNavigate();
  const translation = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [value, setValue] = useState<number | null>(4);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [openDelete, setOpenDelete] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [getRating] = useLazyGetRatingsQuery();
  const [deleteRating] = useDeleteRatingsIdMutation();

  const { isAdd, isDelete } = usePermission({
    label: "Manage Reviews",
    page_permission: "isView",
  });

  const getRatings = async () => {
    setIsLoading(true);
    try {
      const response = await getRating({
        page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
      }).unwrap();
      if (response?.statusCode === 200) {
        setIsLoading(false);
        setRows(response?.data?.list || []);
        setCount(response?.data?.totalCount);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handlePageChange = (event: any, newPage: number) => {
    const updatedPage = newPage + 1;
    setPage(updatedPage);

    const params = new URLSearchParams(window.location.search);
    params.set("page", updatedPage.toString());

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const handleDelete = async () => {
    if (!selectedId) return;

    setIsLoading(true);
    try {
      const response = await deleteRating({ id: selectedId }).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        showToast(translation.error.freelancerDelete);
        setOpenDelete(false);
        getRatings();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRatings();
  }, [page, debouncedSearchTerm]);

  const columns: GridColDef[] = [
    {
      flex: 1,
      minWidth: 220,
      field: "freelancer",
      headerName: translation.main.freelancerDetail,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <div
            className="user_block click_action"
            onClick={() =>
              navigate(
                `/manage-freelancer/detail/${params.row.freelancer?._id}`
              )
            }
          >
            <figure>
              <img
                src={
                  params.row.freelancer?.image ||
                  "/static/images/user_placeholder.png"
                }
                alt=""
              />
            </figure>
            <p>
              <b>{`${params.row.freelancer?.firstName} ${params.row.freelancer?.lastName}`}</b>
              <span>{params.row.freelancer?.email}</span>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 220,
      field: "employer",
      headerName: translation.main.employerDetail,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <div
            className="user_block click_action"
            onClick={() =>
              navigate(`/manage-employers/detail/${params.row.employer?._id}`)
            }
          >
            <figure>
              <img
                src={
                  params.row.employer?.image ||
                  "/static/images/user_placeholder.png"
                }
                alt=""
              />
            </figure>
            <p>
              <b>{`${params.row.employer?.firstName} ${params.row.employer?.lastName}`}</b>
              <span>{params.row.employer?.email}</span>
            </p>
          </div>
        );
      },
    },
    {
      minWidth: 160,
      field: "rating",
      headerName: translation.main.rating,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <Rating
            name="read-only"
            value={params.row.rating}
            readOnly
            className="v2"
          />
        );
      },
    },
    {
      minWidth: 150,
      field: "date",
      headerName: translation.main.dateTime,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return moment(params.row.createdAt).format("DD MMMM YYYY");
      },
    },
    {
      minWidth: 130,
      field: "action",
      headerName: translation.user.Actions,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <div className="table_actions v2">
            {isDelete ? (
              <Box>
                <Tooltip title="delete">
                  <IconButton
                    onClick={() => {
                      setSelectedId(params.row._id);
                      setOpenDelete(true);
                    }}
                  >
                    <img src="/static/images/trash2_icon.svg" alt="" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Manage Ratings</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translation.dashboard.dashboard}
          </span>{" "}
          - {translation.main.manageReview}
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                placeholder={translation.user.SearchHere}
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>
        </div>
        <ConfirmationModal
          text={translation.error.warnFreelancer}
          openModal={openDelete}
          setOpenModal={setOpenDelete}
          handleSubmit={handleDelete}
        />
        <div className="sorting_table">
          <TableComponent
            columns={columns}
            rows={rows}
            loading={isLoading}
            count={count}
            page={page}
            onPageChange={handlePageChange}
          />
          {rows.length !== 0 && (
            <div className="table_footer">
              <div className="pagination_info">
                <span className="total_records">Showing {rows.length} of {count} records</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default ManageReviews;
